import React, { createContext, useContext, useState, ReactNode } from "react";
import { Client } from "../interfaces/client";
import { AdditionalContact } from "../interfaces/additionalContact";
import { PositionCode } from "../interfaces/positionCode";

type ClientRegistrationData = {
  userAgreement: boolean;
  client: Client | null;
  additionalContact: AdditionalContact | null;
  selectedPosCodeType?: string;
  positionCodes: PositionCode[];
  creditReportForm: File | null;
  SSNTraceForm: File | null;
  signatureForm: File | null;
  selectedServices: Map<number, Set<number>>;
};

const createDefaultClient = (): Client => ({
  clientID: 0,
  abbreviation: "",
  phoneNumber: "",
  fax: "",
  clientName: "",
  primaryEmail: "",
  primaryTitle: "",
  primaryFirstName: "",
  primaryLastName: "",
  primaryJobTitle: "",
  address: "",
  address2: "",
  city: "",
  state: "",
  zipCode: "",
  deliveryOptionID: 1,
  isActive: true,
  clientStatusID: 1,
  createdDate: new Date(),
  lastUpdatedDate: new Date(),
  emailNotifications: true,
  discountPercent: 0,
  emailFrequencyID: 0,
  questionnairesReviewed: false,
  referenceFeeTier: 1,
});

const createDefaultAdditionalContact = (): AdditionalContact => ({
  additionalContactID: 0,
  clientID: 0,
  contactEmail: "",
  contactTitle: "",
  contactFirstName: "",
  contactLastName: "",
  contactJobTitle: "",
  invoiceManager: false,
  emailNotifications: false,
});

const defaultClientRegistrationData: ClientRegistrationData = {
  userAgreement: false,
  client: createDefaultClient(),
  additionalContact: createDefaultAdditionalContact(),
  selectedPosCodeType: "",
  positionCodes: [],
  creditReportForm: null,
  SSNTraceForm: null,
  signatureForm: null,
  selectedServices: new Map(),
};

const ClientRegistrationContext = createContext<{
  data: ClientRegistrationData;
  updateData: (newData: Partial<ClientRegistrationData>) => void;
}>({
  data: defaultClientRegistrationData,
  updateData: () => {},
});

export const useRegistrationContext = () =>
  useContext(ClientRegistrationContext);

export const RegistrationProvider = ({ children }: { children: ReactNode }) => {
  const [data, setData] = useState<ClientRegistrationData>(
    defaultClientRegistrationData
  );

  const updateData = (newData: Partial<ClientRegistrationData>) => {
    setData((prev) => ({ ...prev, ...newData }));
  };

  return (
    <ClientRegistrationContext.Provider value={{ data, updateData }}>
      {children}
    </ClientRegistrationContext.Provider>
  );
};
